import type { OutreachRow } from '@discovr/types';
import { isFirstTouchDraft, isInOutreach } from './handoffStates';

const oneLine = (text?: string) => (text ?? '').replace(/\s+/g, ' ').trim();

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Inbox row time (Figma): "11:04" today, "Yesterday", "30 Aug", or "30 Aug 2025". */
export function inboxTime(iso: string | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  if (sameDay(date, now)) {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

/**
 * The message column: the thread subject over the latest reply (else the last
 * sent message); for a creator not yet contacted, whether they can be sent to.
 * "Ready to send" is for a first-touch draft the wizard can send (never to a do-not-contact creator, whose send is refused);
 * an address with no draft says "No draft yet".
 * Null when there's nothing to say (e.g. closed before any message).
 */
export function inboxMessage(row: OutreachRow): { subject?: string; preview: string } | null {
  const subject = row.lastMessage?.subject || undefined;
  if (row.lastReply) {
    const snippet = oneLine(row.lastReply.snippet);
    return { subject, preview: row.lastReply.isAutoReply ? `auto: ${snippet}` : snippet };
  }
  const draft = row.lastMessage;
  if (draft && isInOutreach(row) && row.outreachState === 'sourced' && isFirstTouchDraft(draft.kind, draft.status) && row.hasContact && !row.draftWaitingEmail && !row.suppressed) {
    return { subject, preview: 'Ready to send' };
  }
  if (draft) return { subject, preview: oneLine(draft.snippet) };
  if (!isInOutreach(row) || row.outreachState !== 'sourced') return null;
  if (!row.hasContact) return { preview: 'Add an email to unlock sending' };
  return row.suppressed ? null : { preview: 'No draft yet' };
}
